import type { GetStaticPathsResult } from 'next';
import { useEffect, useState } from 'react';
import { ConfigProvider, useConfig } from 'shared/config/ConfigContext';
import hotelData from 'shared/seo/data/hotel.json';
import { LinkTree } from 'shared/seo/LinkTree';
import { PopularCities } from 'shared/seo/PopularCities';
import { PopularCountries } from 'shared/seo/PopularCountries';
import { validSeoPath } from 'shared/util/formatting';
import { DestinationPage as OldDestinationPage } from '@/components/DestinationPage';
import { cityLevel1Factory, hotelSearchFactory } from '@/components/factory';
import { DestinationPage as NewDestinationPage } from '@/components/NewDestinationPage';
import { Offers } from '@/components/offers/Offers';
import { config, feConfig } from '@/config/index.mjs';
import { getCountriesForDestination, hotelCities, hotelCountries, hotelMapping } from '@/data';
import { verticals } from '@/data/verticals';
import type { CityPageProps, SeoPageContext } from '@/interfaces/pages';
import { pageContentUrl, pageFor } from '@/lib/hotelContent';

const allPagesMetadataUrl = config.get('hotel.allCountryPlaceDestinationPagesMetadata');

const fixParams = (params) => {
    const countryLevel1 = validSeoPath(params.countryLevel1);
    const cityLevel1 = validSeoPath(params.cityLevel1);

    return { countryLevel1, cityLevel1 };
};

export const getStaticPaths = async (): Promise<GetStaticPathsResult> => {
    const pageCombinations = await fetch(allPagesMetadataUrl)
        .then((res) => (res.ok ? res.json() : Promise.reject(`(${res.status}) ${res.statusText}: ${res.url}`)))
        .catch((err) => {
            console.error(err);
            return err;
        });

    const destinations = pageCombinations.map((page: { place: string; country: string }) => ({
        params: { cityLevel1: page.place, countryLevel1: page.country },
    }));

    return {
        paths: destinations,
        // TODO remove this when the prod database has all the entries that the dev database has.
        // I.e. the build pregenerates pages for the entries in the prod database and for entries
        // only in the dev database it will generate them on the fly with fallback set to `blocking`.
        fallback: 'blocking',
    };
};

export const getStaticProps = async (context: SeoPageContext) => {
    const { countryLevel1, cityLevel1 } = fixParams(context.params);
    const page = await pageFor(pageContentUrl.forCountryPlaceDestination(countryLevel1, cityLevel1));

    const destination = hotelCities.find(
        (place) => place.countryPath === countryLevel1.toLowerCase() && place.cityPath === cityLevel1.toLowerCase(),
    );
    const destinationCountry = hotelCountries.find((place) => place.countryPath === countryLevel1.toLowerCase());

    if (!page.htmlContent && (!destination || !destinationCountry)) {
        return {
            notFound: true,
        };
    }

    const popularCities = (hotelMapping[destinationCountry.id] ?? []).filter((mapped) => mapped.city !== destination.city).slice(0, 11);
    const popularCountries = getCountriesForDestination(destinationCountry, hotelCountries);

    return {
        props: {
            destination: {
                ...destination,
                continent: destinationCountry.continent,
            },
            content: page.htmlContent ?? null,
            popularCities,
            popularCountries,
            config: feConfig,
        },
        revalidate: 3600,
    };
};

const Inner = ({ destination, content, popularCities, popularCountries }: Omit<CityPageProps, 'config'>) => {
    const [offers, setOffers] = useState([]);
    const [offersLoading, setOffersLoading] = useState(true);
    const { hotelOfferUrl } = useConfig();
    const offersUrl = `${hotelOfferUrl}?destination=kplace:${destination.placeKey}&rooms=2&pageSize=6&sortField=rating&sortDirection=descending`;

    useEffect(() => {
        fetch(offersUrl)
            .then((res) => (res.ok ? res.json() : Promise.reject(`Fetching offers failed (${res.status}): ${res.url}`)))
            .then((json) => {
                setOffers(json.results ?? []);
                setOffersLoading(false);
            })
            .catch(() => {
                setOffersLoading(false);
            });
    }, [offersUrl]);

    const factory = cityLevel1Factory(destination, hotelData, hotelSearchFactory());

    const DestinationPage = content ? NewDestinationPage : OldDestinationPage;

    return (
        <DestinationPage factory={factory} vertical={verticals.hotel} data={hotelData}>
            {content ? <div className="mb-64" dangerouslySetInnerHTML={{ __html: content }} /> : null}
            <Offers
                loading={offersLoading}
                destination={destination.city}
                placeKey={destination.placeKey}
                offers={offers}
                vertical={verticals.hotel}
                heading={hotelData.lang.headingOffers}
                showMap
            />
            <PopularCities
                destination={destination}
                cities={popularCities}
                cdnUrl={hotelData.cdnPath}
                verticalUrl={hotelData.basePath}
                lang={hotelData.lang}
            />
            <PopularCountries
                destination={destination}
                countries={popularCountries}
                cdnUrl={hotelData.cdnPath}
                verticalUrl={hotelData.basePath}
                lang={hotelData.lang}
            />
            <LinkTree
                heading={hotelData.lang.linkTree.heading}
                description={hotelData.lang.linkTree.description}
                data={hotelData.linkTree}
                url={hotelData.basePath}
            />
        </DestinationPage>
    );
};

const CityLevel1 = ({ destination, content, popularCities, popularCountries, config }: CityPageProps) => (
    <ConfigProvider config={config}>
        <Inner destination={destination} content={content} popularCities={popularCities} popularCountries={popularCountries} />
    </ConfigProvider>
);

export default CityLevel1;
